
import { useAuth } from "@/hooks/use-auth"
import { Button } from "@/components/ui/button"
import { LogOut, Home, Users, History, BookOpen, Settings } from "lucide-react"
import { useSidebar } from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"
import { Avatar, AvatarImage } from "@/components/ui/avatar"
import { Link, useLocation } from "react-router-dom"

import { useTheme } from "@/lib/theme/ThemeContext"

const navigation = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: Home,
  },
  {
    name: "Children",
    href: "/children",
    icon: Users,
  },
  {
    name: "Activities",
    href: "/activities",
    icon: History,
  },
  {
    name: "Settings",
    href: "/settings",
    icon: Settings,
  },
]

export function CaregiverSidebar() {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const { open, setOpen } = useSidebar()
  const isLightTheme = false;

  const handleCloseSidebar = () => {
    setOpen(false)
  }

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div 
          className="fixed inset-0 z-40 bg-black/80 lg:hidden"
          onClick={handleCloseSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-72 shadow-xl lg:static lg:z-auto",
        isLightTheme ? "bg-white border-r border-slate-200" : "bg-slate-900",
        "transform transition-transform duration-200 ease-in-out lg:transform-none",
        open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="flex h-full flex-col">
          {/* Navigation */}
          <nav className="flex-1 space-y-1 p-4">
            {/* Primary Navigation */}
            <div className="space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleCloseSidebar}
                    className={cn(
                      "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive 
                        ? isLightTheme 
                          ? "bg-blue-50 text-[#308CFD] border-l-4 border-[#308CFD]" /* Updated blue from image */
                          : "bg-white/10 text-white" 
                        : isLightTheme
                          ? "text-slate-700 hover:bg-slate-100 hover:text-slate-900" 
                          : "text-slate-400 hover:bg-white/5 hover:text-white"
                    )}
                  >
                    <item.icon className={cn(
                      "h-5 w-5 shrink-0",
                      isActive 
                        ? isLightTheme 
                          ? "text-[#308CFD]" /* Updated blue from image */
                          : "text-white" 
                        : isLightTheme
                          ? "text-slate-500 group-hover:text-slate-700" 
                          : "text-slate-400 group-hover:text-white"
                    )} />
                    {item.name}
                  </Link>
                )
              })}
            </div>
          </nav>

          {/* User Profile & Logout */}
          <div className={cn(
            "p-4 border-t border-white/10"
          )}>
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-9 w-9">
                <AvatarImage src="/assets/default-profile.jpg" />
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className={cn(
                  "text-sm font-medium truncate",
                  isLightTheme ? "text-slate-900" : "text-white"
                )}>
                  {user?.user_metadata?.full_name || "Parent"} 
                </p>
                <p className={cn(
                  "text-xs truncate",
                  isLightTheme ? "text-slate-500" : "text-slate-400"
                )}>
                  {user?.email || "Welcome back!"}
                </p>
              </div>
            </div>

            <Button 
              variant="ghost" 
              className={cn(
                "w-full justify-start",
                isLightTheme 
                  ? "text-red-600 hover:text-red-700 hover:bg-red-50" 
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              )}
              onClick={signOut}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
